import React, { useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  HStack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FaPlus } from "react-icons/fa";

import { loadMoreProfiles } from "../../api/searchApi";

const LoadMoreButton = ({
  searchId,
  buId,
  loadedCount,
  onAppended,
  loadMoreCount = 0,
  maxLoadMorePerSearch = null,
  exhausted = false,
}) => {
  const { t } = useTranslation("portal");
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const cancelRef = useRef(null);

  const capReached =
    maxLoadMorePerSearch != null && loadMoreCount >= maxLoadMorePerSearch;

  const fire = async () => {
    setIsLoading(true);
    try {
      const result = await loadMoreProfiles(searchId, { buId });
      onAppended?.(result);
      if ((result?.newCount ?? 0) === 0) {
        toast({
          title: t("asapProfile.loadMore.noMoreToast"),
          status: "info",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: t("asapProfile.loadMore.successToast", {
            count: result.newCount,
          }),
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error("Load more failed", err);
      const detail = err?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : detail;
      if (code === "PDL_LOAD_MORE_LIMIT_REACHED") {
        toast({
          title: t("asapProfile.loadMore.limitReachedToast", {
            cap: detail?.maxLoadMorePerSearch ?? maxLoadMorePerSearch ?? "",
          }),
          status: "warning",
          duration: 6000,
          isClosable: true,
        });
      } else {
        toast({
          title: t("asapProfile.loadMore.errorToast"),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = () => {
    if (capReached) {
      // Don't even attempt — show a friendly message instead.
      toast({
        title: t("asapProfile.loadMore.limitReachedToast", {
          cap: maxLoadMorePerSearch ?? "",
        }),
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    // Confirm before second-and-later billable extracts. We use loadedCount
    // > 12 as the cue for "user is now stacking extracts".
    if (loadedCount > 12) {
      setConfirmOpen(true);
    } else {
      fire();
    }
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    fire();
  };

  // When the descending ladder has no further rungs to resume, a Load +12
  // would return nothing. Replace the button with a clear, terminal message
  // so the user understands the search is complete and does not keep clicking
  // (and is not led to think the feature is broken).
  if (exhausted) {
    return (
      <HStack justify="center" pt={4} data-testid="load-more-exhausted">
        <Text
          fontSize="sm"
          color="gray.500"
          textAlign="center"
          sx={{ marginBottom: "0 !important" }}
        >
          {t("asapProfile.loadMore.exhausted", { count: loadedCount })}
        </Text>
      </HStack>
    );
  }

  return (
    <>
      <HStack justify="center" pt={4}>
        <Button
          leftIcon={<FaPlus />}
          variant="outline"
          colorScheme="teal"
          isLoading={isLoading}
          onClick={handleClick}
          isDisabled={capReached}
          data-testid="load-more-button"
        >
          {t("asapProfile.loadMore.label")}
        </Button>
        <Text fontSize="xs" color="gray.500" sx={{ marginBottom: "0 !important" }}>
          {t("asapProfile.loadMore.loadedCount", { count: loadedCount })}
        </Text>
        {maxLoadMorePerSearch != null && maxLoadMorePerSearch > 0 && (
          <Text fontSize="xs" color="gray.500" sx={{ marginBottom: "0 !important" }}>
            {t("asapProfile.loadMore.callsRemaining", {
              count: Math.max(maxLoadMorePerSearch - loadMoreCount, 0),
            })}
          </Text>
        )}
      </HStack>

      <AlertDialog
        isOpen={confirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setConfirmOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent data-testid="load-more-rebill-dialog">
            <AlertDialogHeader sx={{ marginBottom: "0 !important" }}>
              {t("asapProfile.loadMore.rebillDialog.title")}
            </AlertDialogHeader>
            <AlertDialogBody>
              {t("asapProfile.loadMore.rebillDialog.body")}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setConfirmOpen(false)}>
                {t("asapProfile.loadMore.rebillDialog.cancel")}
              </Button>
              <Button colorScheme="teal" ml={3} onClick={handleConfirm}>
                {t("asapProfile.loadMore.rebillDialog.confirm")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default LoadMoreButton;
